import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { BarChart } from '../components/charts/BarChart';
import { interpolateColors } from '../components/charts/colors';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';

interface KpiItem {
  label: string;
  value: number;
  type: 'money' | 'count' | 'percent';
}

interface BreakdownItem {
  label: string;
  value: number;
}

interface GlReconciliationRow {
  label: string;
  sub_ledger: number;
  gl_posted: number;
}

interface FinanceStats {
  kpis: KpiItem[];
  gl_reconciliation: GlReconciliationRow[];
  purchase_requests_by_stage: BreakdownItem[];
}

const GREEN_RAMP_STOPS = ['#60aa45', '#3f7a29', '#2c591c'];
// Two-series comparison (Sub-ledger vs GL Posted) — see Dashboard.tsx's
// original comment: validated CVD-safe, worst adjacent ΔE 24.8.
const GL_SERIES_COLORS = ['#3f7a29', '#2a78d6'];

export function FinanceDashboard() {
  const { hasPermission } = useAuth();
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const [stats, setStats] = useState<FinanceStats | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<FinanceStats>('/analytics/finance/stats')
      .then(({ data }) => setStats(data))
      .catch((err: ApiError) => setError(err.message || 'Could not load the dashboard.'));
  }, []);

  const kpi = (label: string) => stats?.kpis.find((k) => k.label === label);
  const fmtKpi = (label: string) => {
    const item = kpi(label);
    if (!item) return '—';
    return item.type === 'money' ? fmtMoney(item.value) : item.value;
  };

  const reportLink = (permission: string, to: string) =>
    hasPermission(permission) && (
      <div className="chart-card-footer">
        <Link to={to}>View full report →</Link>
      </div>
    );

  return (
    <>
      <header className="page-header">
        <h1>Finance & Accounting Dashboard</h1>
      </header>
      <p className="muted">A snapshot of revenue, GL reconciliation, and open purchase requests.</p>

      {error && <div className="alert alert-error">{error}</div>}
      {!error && !stats && <p className="muted">Loading…</p>}

      {stats && (
        <>
          <div className="stat-grid">
            {['Revenue Collected (This Month)', 'GL Reconciliation Variance (This Month)', 'Open Purchase Requests', 'Open Purchase Requests Value'].map(
              (label) => (
                <div className="card stat" key={label}>
                  <span className="stat-label">{label}</span>
                  <span className="stat-value">{fmtKpi(label)}</span>
                </div>
              )
            )}
          </div>

          <div className="chart-grid">
            <div className="card">
              <div className="chart-card-header">
                <h2>GL Reconciliation (This Month)</h2>
              </div>
              <div className="chart-card-hero">{fmtKpi('GL Reconciliation Variance (This Month)')}</div>
              <BarChart
                data={stats.gl_reconciliation.map((r) => ({ label: r.label, values: [r.sub_ledger, r.gl_posted] }))}
                seriesColors={GL_SERIES_COLORS}
                seriesLabels={['Sub-ledger', 'GL Posted']}
                formatValue={fmtMoney}
              />
              {reportLink('gl_interface.reports.view', '/reports/gl/reconciliation')}
            </div>

            <div className="card">
              <div className="chart-card-header">
                <h2>Open Purchase Requests</h2>
              </div>
              <div className="chart-card-hero">
                {fmtKpi('Open Purchase Requests')} · {fmtKpi('Open Purchase Requests Value')}
              </div>
              <BarChart
                data={stats.purchase_requests_by_stage.map((a) => ({ label: a.label, values: [a.value] }))}
                seriesColors={interpolateColors(GREEN_RAMP_STOPS, stats.purchase_requests_by_stage.length)}
                formatValue={(n) => String(n)}
              />
              {reportLink('procurement.reports.view', '/reports/procurement/request-pipeline')}
            </div>
          </div>
        </>
      )}
    </>
  );
}
